import { Controller } from "@hotwired/stimulus"

import { onlyDigits, formatCpf, isValidCpfDigits, formatIsoFromBrDate } from "../../modules/crud/consulta_ws/helpers"
import { lockScreen } from "../../modules/crud/consulta_ws/overlay"
import { applySoaResponseToForm, clearSoaFields } from "../../modules/crud/consulta_ws/mapper"
import { setFormDisabled } from "../../modules/crud/consulta_ws/form_disable"

// Controller: crud--consulta-web-service
export default class extends Controller {
  static targets = ["input", "msg"]
  static values = {
    sourceUrl: String,
    localCheckUrl: String,
    admissaoId: String,
  }

  connect() {
    if (typeof window.$j === "undefined") return
    this.$j = window.$j

    // evita reinicializar (Turbo reconecta/visita)
    if (this.element.dataset.consultaWsInited === "true") return
    this.element.dataset.consultaWsInited = "true"

    this.lastCpf = null
    this.inFlight = null
    this.soaSuccess = false

    // aplica máscara se já veio preenchido
    const v = this.inputTarget.value
    if (v) {
      this.inputTarget.value = formatCpf(v)
      // Admissão já salva: trava o CPF imediatamente (não permite alterar)
      if (this.admissaoIdValue) this._lockCpf()
    }
  }

  disconnect() {
    // aborta chamada pendente ao sair da página
    if (this.inFlight && typeof this.inFlight.abort === "function") {
      try { this.inFlight.abort() } catch (_e) { }
    }
    this.inFlight = null
  }

  // input->...#mask
  mask() {
    const v = this.inputTarget.value
    const f = formatCpf(v)
    if (v !== f) this.inputTarget.value = f
  }

  // blur/change->...#consult
  consult() {
    if (!this.$j) return

    const cpfDigits = onlyDigits(this.inputTarget.value)

    if (!cpfDigits || cpfDigits.length < 11) {
      this.setMsg(null)
      this.lastCpf = null
      return
    }

    if (!isValidCpfDigits(cpfDigits)) {
      this.setMsg("CPF inválido. Verifique e tente novamente.")
      this.lastCpf = null
      return
    }

    if (cpfDigits === this.lastCpf) return

    this.lastCpf = cpfDigits

    const url = this.sourceUrlValue
    if (!url) return

    // aborta request anterior
    if (this.inFlight && typeof this.inFlight.abort === "function") {
      try { this.inFlight.abort() } catch (_e) { }
    }

    this.setMsg(null)
    this.soaSuccess = false

    const $j = this.$j
    const $cpf = $j(this.inputTarget)
    const $form = $cpf.closest("form")

    // Novo CPF: limpa dados da consulta anterior e desbloquia campo
    clearSoaFields($j, $form)
    this._unlockCpf()

    lockScreen(true)
    $form.addClass("admissao-consultando")
    setFormDisabled($j, $form, true, { keepEnabled: [$cpf] })

    this.inFlight = $j.ajax({
      url,
      type: "GET",
      dataType: "json",
      data: { documento: cpfDigits, pula_senha: true },
    })

    this.inFlight.done((response) => {
      const dados = response && response.dados ? response.dados : null
      if (!dados) {
        this.setMsg("Resposta inválida do serviço.")
        return
      }

      if (dados.status === false) {
        this.setMsg(dados.mensagem || "Não foi possível consultar o CPF.")
        return
      }

      // aplica mapeamento no formulário
      applySoaResponseToForm($j, $form, dados, {
        cpfDigits,
        formatIsoFromBrDate,
        formatCpf,
      })

      this.setMsg(null)
      this.soaSuccess = true
    })

    this.inFlight.fail((xhr) => {
      if (xhr && xhr.status === 0) return // abort
      this.setMsg("Falha ao consultar o CPF. Tente novamente.")
    })

    this.inFlight.always(() => {
      lockScreen(false)
      setFormDisabled($j, $form, false)
      $form.removeClass("admissao-consultando")
      this.inFlight = null

      if (this.hasLocalCheckUrlValue) this._checkLocal(cpfDigits)
    })
  }

  _checkLocal(cpfDigits) {
    const url = this.localCheckUrlValue
    const admissaoId = this.admissaoIdValue || ""
    const $j = this.$j
    const $form = $j(this.element).closest("form")

    $j.ajax({
      url,
      type: "GET",
      dataType: "json",
      data: { cpf: cpfDigits, admissao_id: admissaoId },
    }).done((res) => {
      if (res && res.bloqueado) {
        this.setMsg(res.motivo)
        this.inputTarget.classList.add("border-red-500", "ring-1", "ring-red-300")
        this.inputTarget.classList.remove("border-gray-300")
        $form.trigger("cpf:bloqueado")
        // CPF fica editável para o usuário corrigir
        this._unlockCpf()
      } else {
        this.setMsg(null)
        this.inputTarget.classList.remove("border-red-500", "ring-1", "ring-red-300")
        this.inputTarget.classList.add("border-gray-300")
        $form.trigger("cpf:liberado")
        // Trava o CPF após consulta bem-sucedida (SOA ok + sem bloqueio local)
        if (this.soaSuccess) this._lockCpf()
      }
    })
  }

  _lockCpf() {
    this._setCpfLocked(true)
  }

  _unlockCpf() {
    this._setCpfLocked(false)
  }

  _setCpfLocked(locked) {
    const els = [this.inputTarget]

    // Campo CPF na seção de Documentos (preenchido pelo mapper SOA)
    if (this.$j) {
      const $docCpf = this.$j(this.inputTarget).closest("form").find("#params_cpf")
      if ($docCpf.length) els.push($docCpf[0])
    }

    els.forEach((el) => {
      if (!el) return
      el.readOnly = locked
      if (locked) {
        el.classList.add("bg-gray-100", "cursor-not-allowed")
      } else {
        el.classList.remove("bg-gray-100", "cursor-not-allowed")
      }
    })
  }

  setMsg(text) {
    const $j = this.$j
    if (!this.hasMsgTarget) return

    const $msg = $j(this.msgTarget)

    if (!text) {
      $msg.hide().text("")
    } else {
      $msg.text(text).show()
    }
  }
}
